import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Package,
  Hash,
  CheckCircle,
  Tag,
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { ProductRow } from './ProductRow';

const ProductTable = ({
  products = [],
  categories = [],
  isLoading = false,
  error = null,
  onEdit,
  onDelete,
  onRefresh,
  // Pagination
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  startIndex,
  endIndex,
  onPageChange,
  onItemsPerPageChange
}) => {
  // Ensure products is always an array
  const safeProducts = Array.isArray(products) ? products : [];
  const safeCategories = Array.isArray(categories) ? categories : [];

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-800">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
              Error al cargar productos
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              {error}
            </p>
            {onRefresh && (
              <Button 
                onClick={onRefresh} 
                variant="outline" 
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reintentar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 shadow-md">
      <CardHeader className="bg-gradient-to-r from-orange-50/50 to-amber-50/30 dark:from-orange-900/20 dark:to-amber-900/20 border-b border-orange-100/50 dark:border-orange-800/30">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
            <Package className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl text-slate-900 dark:text-slate-100">
              Productos
            </CardTitle>
            <CardDescription className="text-slate-600 dark:text-slate-400">
              {isLoading ? "Cargando..." : `${totalItems} productos en total`}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Cargando productos...
              </p>
            </div>
          </div>
        ) : safeProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mb-4">
              <Package className="h-8 w-8 text-orange-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              No hay productos
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Comienza agregando tu primer producto.
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50/50 dark:bg-slate-800/50">
                  <TableRow className="border-b border-slate-200/60 dark:border-slate-700/60">
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                      <div className="flex items-center space-x-2">
                        <Package className="h-4 w-4 text-orange-500" />
                        <span>Nombre</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300">
                      <div className="flex items-center space-x-2">
                        <Hash className="h-4 w-4 text-orange-500" />
                        <span>SKU</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-orange-500" />
                        <span>Precio</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <Layers className="h-4 w-4 text-orange-500" />
                        <span>Items</span>
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-slate-700 dark:text-slate-300 text-center">
                      Acciones
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {safeProducts.map((product, index) => (
                    <ProductRow
                      key={product.id || index}
                      product={product}
                      onEdit={onEdit}
                      onDelete={onDelete}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-slate-200/60 dark:border-slate-700/60 bg-slate-50/30 dark:bg-slate-800/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Mostrando{" "}
                      <span className="font-semibold text-orange-600">
                        {startIndex}
                      </span>{" "}
                      a{" "}
                      <span className="font-semibold text-orange-600">
                        {endIndex}
                      </span>{" "}
                      de{" "}
                      <span className="font-semibold text-orange-600">
                        {totalItems}
                      </span>{" "}
                      productos
                    </span>

                    <Select
                      value={itemsPerPage.toString()}
                      onValueChange={(value) =>
                        onItemsPerPageChange(parseInt(value))
                      }
                    >
                      <SelectTrigger className="w-24 h-8 border-slate-200/60 dark:border-slate-700/60">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5</SelectItem>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    <span className="px-3 py-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                      {currentPage} de {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onPageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0 border-slate-200/60 dark:border-slate-700/60 hover:bg-orange-50 hover:border-orange-300"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductTable; 